import { logFirebaseEvent } from './client';
import {
  AnalyticsEventNames,
  type AnalyticsItem,
  type AnalyticsEventName,
  type AnalyticsEventParams,
} from './event-map';
import {
  getCurrentAnalyticsPageContext,
  type AnalyticsPageContext,
} from './page-context';

type SanitizedAnalyticsParams = Record<
  string,
  string | number | AnalyticsItem[]
>;

const sanitizeItems = (items: AnalyticsItem[]): AnalyticsItem[] =>
  items.map(item => ({
    item_id: item.item_id,
    item_name: item.item_name,
    ...(typeof item.price === 'number' && Number.isFinite(item.price)
      ? { price: item.price }
      : {}),
    ...(typeof item.quantity === 'number' && Number.isFinite(item.quantity)
      ? { quantity: item.quantity }
      : {}),
  }));

const sanitizeParams = (
  params?: AnalyticsEventParams
): SanitizedAnalyticsParams | undefined => {
  if (!params) {
    return undefined;
  }

  const sanitized: SanitizedAnalyticsParams = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        sanitized[key] = sanitizeItems(value);
      }
      continue;
    }

    if (typeof value === 'number' && !Number.isFinite(value)) {
      continue;
    }

    sanitized[key] = typeof value === 'boolean' ? String(value) : value;
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

export const trackEvent = async (
  eventName: AnalyticsEventName,
  params?: AnalyticsEventParams,
  pageContext: AnalyticsPageContext = getCurrentAnalyticsPageContext()
): Promise<void> => {
  await logFirebaseEvent(eventName, {
    ...sanitizeParams(params),
    ...pageContext,
  });
};

export const trackPageView = async (
  pageContext: AnalyticsPageContext = getCurrentAnalyticsPageContext()
): Promise<void> => {
  await trackEvent(AnalyticsEventNames.PAGE_VIEW, undefined, pageContext);
};
